import type { HTMLProps } from 'react';

import { useIsMutating } from '@tanstack/react-query';

import { Select, Trans, useLingui } from '@lingui/react/macro';
import type { useCart } from 'src/api/queries';
import { queryKeys } from 'src/api/queries';
import { Spinner } from 'src/components';
import { formatAmount } from 'src/utils/productUtils';

interface PriceProps extends HTMLProps<HTMLDivElement> {
  cart: ReturnType<typeof useCart>;
  showTaxStatus?: boolean;
}
export const Price = ({ cart, showTaxStatus, ...props }: PriceProps) => {
  const { i18n } = useLingui();
  const isMutatingCurrency = useIsMutating({
    mutationKey: queryKeys.checkout.currency,
  });
  const isMutatingTax = useIsMutating({ mutationKey: queryKeys.checkout.tax });

  const formattedPrice = formatAmount(
    cart.details.data?.data.productPrice.total ?? 0,
    cart.details.data?.data.currency,
    i18n.locale
  );

  if (cart.details.isError) {
    return (
      <span {...props}>
        <Trans
          context="Subscription price display"
          comment="Displayed when there is an error calculating the price of the subscription."
        >
          (could not calculate tax)
        </Trans>
      </span>
    );
  }

  if (isMutatingCurrency || isMutatingTax || cart.updateCurrency.isPending) {
    return (
      <span {...props}>
        <Spinner inline />
      </span>
    );
  }

  function getTaxDisplayVariation() {
    if (showTaxStatus === false) {
      return 'none';
    } else if (cart.details.data?.data.tax.isIncluded) {
      return 'withVat';
    } else if (cart.details.data?.data.tax.isCalculated) {
      return 'withTax';
    } else {
      return 'withoutTax';
    }
  }

  return (
    <span {...props}>
      <Select
        context="Subscription price display"
        comment="Subscription price per month with or without tax or VAT"
        value={getTaxDisplayVariation()}
        _withVat={<Trans>{formattedPrice}/mo. (incl. VAT)</Trans>}
        _withTax={<Trans>{formattedPrice}/mo. (incl. tax)</Trans>}
        _withoutTax={<Trans>{formattedPrice}/mo. (excl. tax)</Trans>}
        other={<Trans>{formattedPrice}/mo.</Trans>}
      />
    </span>
  );
};
